import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { User, CreditCard, Download, FileX, UserCog, StickyNote, Lock, Eye, ListChecks, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/control/PageHeader";
import { PassBody } from "@/components/control/AccessPassDialog";
import { api } from "@/lib/api";
import {
  Section, Loadable, Empty, Status, Info, History, ActionButton, BackLink, SelectField, Tabbar, TabPanel, useTabs, useLoad, fmt, fmtDay, deText, saveBlob,
  PageView, PageRows, PageButtons, BlockedAside, label,
} from "@/components/control/kit";

const EXPORT_FIELDS = [
  ["name", "Name"], ["city", "Ort"], ["country", "Land"], ["languages", "Sprachen"], ["organization", "Organisation"],
  ["status", "Status"], ["verification", "Profilprüfung"], ["joined_at", "Mitglied seit"], ["email", "E-Mail"],
].map(([value, label]) => ({ value, label }));

function StatusAction({ m, onDone }) {
  return (
    <ActionButton label="Status ändern" icon={UserCog} testId="member-status-change" danger onDone={onDone} submitLabel="Status speichern"
      page="status" title="Mitgliedsstatus ändern" pageSubtitle="Eine Einschränkung benötigt Grund, Umfang und festgelegten Zeitraum."
      pageRows={[["Mitglied", m.name]]} pageSubject={m.name}
      pageNotice={{ title: "Entscheidung noch offen", text: "Begründung und Nachricht vor einer bestätigten Massnahme prüfen." }}
      description="Der Grund wird im Verlauf und im Prüfprotokoll gespeichert. Eine Sperre beendet alle Sitzungen des Mitglieds."
      fields={[
        { name: "status", label: "Neuer Status", type: "select", required: true, options: [
          { value: "aktiv", label: "Aktiv (Einschränkungen aufheben)" }, { value: "eingeschränkt", label: "Schreibrecht vorübergehend einschränken" },
          { value: "gesperrt", label: "Konto sperren" }] },
        { name: "days", label: "Dauer der Einschränkung (Tage)", type: "number", default: "7", help: "Nur bei Einschränkung, 1–90 Tage." },
        { name: "reason", label: "Grund", type: "textarea", required: true },
        { name: "internal_note", label: "Interne Notiz (optional)", type: "textarea" },
      ]}
      onSubmit={async (v) => { await api.post(`/members/${m.id}/status`, { ...v, days: Number(v.days) || undefined }); return "Status geändert."; }} />
  );
}

function ExportAction({ m }) {
  return (
    <ActionButton label="Daten exportieren" icon={Download} testId="member-export" submitLabel="Export prüfen und erstellen"
      page="export" title="Mitgliedsdaten exportieren" pageSubtitle="Prüfe Zweck, Identität und erforderlichen Umfang des Mitgliedsexports."
      pageRows={[["Mitglied", m.name], ["Privater Access Pass", "Nicht im Export enthalten"]]} pageSubject={m.name}
      description="Nur erlaubte Felder. Access Pass, private Nachrichten und interne Notizen sind nie enthalten."
      fields={[
        { name: "purpose", label: "Zweck des Exports", type: "textarea", required: true },
        { name: "fields", label: "Felder", type: "checks", options: EXPORT_FIELDS, required: true },
        { name: "confirm", label: "Ausgewählten Datenumfang vor Bereitstellung geprüft", type: "checkbox", required: true },
      ]}
      onSubmit={async ({ confirm, ...v }) => {
        const { data } = await api.post(`/members/${m.id}/export`, v);
        saveBlob(JSON.stringify(data.export, null, 2), `mitglied-${m.id}.json`, "application/json");
        return { message: "Export erstellt und protokolliert.", show: <p className="text-sm">Datei heruntergeladen. Ausgeschlossen: {data.excluded.join(", ")}.</p> };
      }} />
  );
}

function DataRequestAction({ m }) {
  return (
    <ActionButton label="Datenanfrage erfassen" icon={FileX} testId="member-data-request" submitLabel="Anfrage erfassen"
      description="Kontolöschung oder Auskunft. Vor jeder Bearbeitung wird die Identität geprüft (Einstellungen → Datenanfragen)."
      fields={[
        { name: "type", label: "Art", type: "select", required: true, options: [{ value: "loeschung", label: "Kontolöschung" }, { value: "auskunft", label: "Auskunft" }] },
        { name: "note", label: "Notiz", type: "textarea" },
      ]}
      onSubmit={async (v) => { await api.post(`/members/${m.id}/data-requests`, v); return "Datenanfrage erfasst."; }} />
  );
}

function Participation({ id }) {
  const [days, setDays] = useState("180");
  const st = useLoad(`/members/${id}/participation?days=${days}`);
  return (
    <Section title="Teilnahmeverlauf" testId="member-participation" actions={
      <SelectField id="part-days" label="Zeitraum" value={days} onChange={setDays} testId="participation-range"
        options={[{ value: "90", label: "90 Tage" }, { value: "180", label: "180 Tage" }, { value: "365", label: "12 Monate" }]} />
    }>
      <Loadable state={st}>
        {(d) => (d.items.length === 0 ? <Empty text="Keine Teilnahmen im Zeitraum." /> : (
          <ul className="divide-y divide-border">
            {d.items.map((i) => (
              <li key={i.activity_id} className="flex items-center justify-between gap-2 py-2.5">
                <div><p className="font-medium text-brand-purple">{i.title}</p><p className="text-xs text-muted-foreground">{fmt(i.date)}</p></div>
                <div className="flex gap-1.5"><Status value={i.participation} /><Status value={i.activity_status} /></div>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

function Notes({ id, name }) {
  const st = useLoad(`/members/${id}/notes`);
  return (
    <Section title="Interne Notizen" testId="member-notes" actions={
      <ActionButton label="Notiz hinzufügen" icon={StickyNote} testId="add-member-note" onDone={st.reload}
        page="notiz" title="Interne Mitgliedsnotiz" pageSubtitle="Diese interne Notiz wird nicht als Nachricht an das Mitglied versendet."
        pageSubject={name} pageRows={[["Mitglied", name], ["Sichtbarkeit", "Berechtigtes Support-Team · nie für das Mitglied"],
          ["Autor", "Deine Anmeldung · mit Zeitpunkt dokumentiert"]]}
        description="Nur für berechtigte Teammitglieder sichtbar, nie für das Mitglied. Keine Gesundheitsdaten eintragen."
        fields={[{ name: "text", label: "Notiz", type: "textarea", required: true }]}
        onSubmit={async (v) => { await api.post(`/members/${id}/notes`, v); return "Notiz gespeichert."; }} />
    }>
      <Loadable state={st}>
        {(d) => (d.notes.length === 0 ? <Empty text="Noch keine Notizen." /> : (
          <ul className="space-y-3">
            {d.notes.map((n) => (
              <li key={n.id} className="rounded-lg bg-secondary/60 p-3" data-testid={`member-note-${n.id}`}>
                <p className="text-sm text-zinc-800">{n.text}</p>
                <p className="mt-1 text-xs text-muted-foreground">{n.author?.name} · {fmt(n.created_at)}</p>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </Section>
  );
}

function PassCard({ d, onOpen }) {
  return (
    <Section title={<span className="flex items-center gap-2"><Lock className="h-5 w-5 text-brand-magenta" aria-hidden="true" /> Privater Pass</span>} testId="member-pass-card">
      <p className="text-sm text-zinc-700">Der Access Pass enthält vertrauliche Angaben und ist nur nach einer persönlichen, zweckgebundenen Freigabe sichtbar. Unterstützungswünsche erscheinen nie im öffentlichen Profil.</p>
      <p className="mt-2 text-sm">Stand: <strong data-testid="member-pass-state">{d.access_pass.state === "aktiv" ? "Freigabe für dich aktiv" : "keine Freigabe für dich"}</strong></p>
      <Button size="sm" onClick={onOpen} data-testid="member-pass-open" className="mt-3 gap-1.5 bg-brand-magenta hover:bg-brand-magenta-dark">
        <CreditCard className="h-4 w-4" aria-hidden="true" /> {d.access_pass.state === "aktiv" ? "Freigabe ansehen" : "Zugriff anfragen"}
      </Button>
    </Section>
  );
}

function ProfileTab({ d, onPass }) {
  const m = d.member;
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      <Section title="Öffentliche Profilangaben" className="lg:col-span-2" testId="member-profile">
        <Info items={[
          ["Status", <Status value={m.status} />], ["Profilprüfung", <Status value={m.verification} />], ["Land", m.country],
          ["Sprachen", (m.languages || []).join(", ")], ["Mitglied seit", fmtDay(m.joined_at)],
          ["Schreibrecht", m.write_restricted_until ? `eingeschränkt bis ${fmt(m.write_restricted_until)}` : "uneingeschränkt"],
          d.can.manage && ["E-Mail", m.email], ["Interessen", (m.interests || []).map(deText).join(", ")], ["Über mich", deText(m.bio)],
        ]} />
      </Section>
      <div className="space-y-5">
        <PassCard d={d} onOpen={onPass} />
        <Section title="Kontakt- und Sichtbarkeitsrechte" testId="member-visibility">
          <ul className="space-y-2.5">
            {d.visibility.map((v) => (
              <li key={v.key} className="flex items-center justify-between gap-2 text-sm"><span>{v.label}</span><Status value={v.value} /></li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">Diese Einstellungen legt das Mitglied selbst in der App fest. Das Team kann sie nicht ändern.</p>
        </Section>
      </div>
    </div>
  );
}

function HistoryTab({ d }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Section title="Statusverlauf" testId="member-status-history">
        {d.can.manage ? <History items={d.member.status_history} testId="member-status-history-list" />
          : <Empty text="Der Statusverlauf ist nur für die Mitgliederverwaltung sichtbar." testId="member-status-history-restricted" />}
      </Section>
      {d.can.notes ? <Notes id={d.member.id} name={d.member.name} /> : (
        <Section title="Interne Notizen" testId="member-notes"><Empty text="Deine Rolle hat keinen Zugriff auf interne Notizen." testId="member-notes-restricted" /></Section>
      )}
    </div>
  );
}

const MEMBER_TABS = [{ value: "profil", label: "Profil" }, { value: "aktivitaeten", label: "Aktivitäten" }, { value: "verlauf", label: "Verlauf" }];

// C33: the profile as its own page, with public data only; the private pass stays outside.
function ProfilePage({ d }) {
  const m = d.member;
  const place = [m.city, m.country].filter(Boolean).join(" · ") || "–";
  return (
    <PageView page="profil" label="Profil im Detail" icon={Eye} testId="member-profile-view" title="Mitgliedsprofil im Detail"
      subtitle="Diese Verwaltungsansicht enthält den erforderlichen Mitgliedskontext." subject={m.name}>
      {({ back }) => (
        <>
          <div className="rounded-2xl border border-[#E6E1F0] bg-white p-5" data-testid="member-profile-hero">
            <p className="text-2xl font-bold text-brand-purple">{m.name}</p>
            <p className="mt-1 text-sm text-zinc-600">{place}</p>
            <span className="mt-2 inline-flex rounded-full bg-[#EEEAF7] px-2.5 py-0.5 text-xs font-semibold text-brand-purple">Öffentliche Profilangaben</span>
          </div>
          <PageRows testId="member-profile-view" rows={[
            ["Mitglied", m.name], ["Stadt", place], ["Konto", label(m.status)], ["Profilprüfung", label(m.verification)],
            ["Mitglied seit", fmtDay(m.joined_at)], ["Privater Access Pass", "Nicht Teil des allgemeinen Profils"]]} />
          <PageButtons primary={d.can.manage ? "Status ändern" : null} primaryTo={`/mitglieder/${m.id}/status`} back={back} testId="member-profile-view" />
        </>
      )}
    </PageView>
  );
}

// C34: participation history as its own page; separates confirmed, requested and cancelled.
function ParticipationPage({ m }) {
  return (
    <PageView page="teilnahmen" label="Teilnahmeverlauf" icon={ListChecks} testId="member-participation-view" title="Teilnahmeverlauf eines Mitglieds"
      subtitle="Unterscheide Zusage, Anfrage und Absage im Teilnahmeverlauf." subject={m.name}>
      {({ back }) => <><ParticipationRows id={m.id} /><PageButtons back={back} testId="member-participation-view" /></>}
    </PageView>
  );
}

function ParticipationRows({ id }) {
  const st = useLoad(`/members/${id}/participation?days=365`);
  return (
    <Loadable state={st}>
      {(d) => (d.items.length === 0 ? <Empty text="Keine Teilnahmen in den letzten 12 Monaten." testId="member-participation-empty" /> : (
        <ul className="space-y-2">
          {d.items.map((i) => (
            <li key={i.activity_id} className="flex items-center justify-between gap-3 rounded-xl border border-[#E6E1F0] bg-white px-4 py-3" data-testid={`mp-row-${i.activity_id}`}>
              <div className="min-w-0"><p className="break-words font-bold text-brand-purple">{i.title}</p>
                <p className="text-sm text-zinc-600">{fmt(i.date)} · {label(i.participation)}{i.activity_status && i.activity_status !== "veröffentlicht" ? ` · Aktivität ${label(i.activity_status)}` : ""}</p></div>
              <Link to={`/aktivitaeten/${i.activity_id}`} className="shrink-0 text-sm font-bold text-brand-magenta hover:underline" data-testid={`mp-open-${i.activity_id}`}>Öffnen</Link>
            </li>
          ))}
        </ul>
      ))}
    </Loadable>
  );
}

// C35: visibility is decided by the member; the team only reads it.
function VisibilityPage({ d }) {
  const m = d.member;
  return (
    <PageView page="sichtbarkeit" label="Kontakt & Sichtbarkeit" icon={ShieldCheck} testId="member-visibility-view" title="Kontakt- und Sichtbarkeitsrechte"
      subtitle="Sichtbarkeit wird durch die Entscheidungen des Mitglieds bestimmt." subject={m.name}
      aside={<BlockedAside icon={Eye} title="Nur lesend" text="Diese Einstellungen legt das Mitglied selbst in der App fest. Das Team kann sie nicht ändern." lavender />}>
      {({ back }) => (
        <>
          <PageRows testId="member-visibility-view" rows={d.visibility.map((v) => [v.label, /access/i.test(v.label) ? "Privat · Gesonderte persönliche Freigabe" : label(v.value)])} />
          <PageButtons primary="Zur Mitgliedsübersicht" primaryTo="/mitglieder" back={back} testId="member-visibility-view" />
        </>
      )}
    </PageView>
  );
}

function AccessPassPage({ d, reload }) {
  const m = d.member;
  return (
    <PageView page="access-pass" label={`Access Pass (${d.access_pass.state === "aktiv" ? "Freigabe aktiv" : "privat"})`} icon={CreditCard}
      testId="member-access-pass" title="Privater Access Pass" subject={m.name}
      subtitle="Passdaten sind privat. Sichtbar nur mit persönlicher, zweckgebundener und befristeter Freigabe des Mitglieds.">
      {({ back }) => <><PassBody member={m} onChange={reload} /><PageButtons back={back} testId="member-access-pass" /></>}
    </PageView>
  );
}

function MemberView({ d, reload }) {
  const m = d.member;
  const navigate = useNavigate();
  const { aktion } = useParams();
  const [tab, setTab] = useState(aktion === "notiz" ? "verlauf" : "profil");
  const tabs = useTabs(MEMBER_TABS, tab);
  return (
    <>
      <PageHeader title={m.name} subtitle={`${m.city || "–"} · ${m.organization || "Keine Organisation"}`} icon={User} actions={<>
        {d.can.manage && <StatusAction m={m} onDone={reload} />}
        {d.can.export && <ExportAction m={m} />}
        {d.can.manage && <DataRequestAction m={m} />}
        <AccessPassPage d={d} reload={reload} />
      </>} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="member-views">
        <ProfilePage d={d} />
        <ParticipationPage m={m} />
        <VisibilityPage d={d} />
      </div>
      <Tabbar tabs={tabs} onChange={setTab} testId="member-tabs" ariaLabel="Bereiche des Mitgliedsprofils" />
      <TabPanel tabs={tabs} testId="member-tabs-panel">
        {tabs.value === "profil" && <ProfileTab d={d} onPass={() => navigate(`/mitglieder/${m.id}/access-pass`)} />}
        {tabs.value === "aktivitaeten" && <Participation id={m.id} />}
        {tabs.value === "verlauf" && <HistoryTab d={d} />}
      </TabPanel>
    </>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const st = useLoad(`/members/${id}`);
  return (
    <div>
      <BackLink to="/mitglieder">Zurück zu Mitglieder</BackLink>
      <Loadable state={st}>{(d) => <MemberView key={d.member.id} d={d} reload={st.reload} />}</Loadable>
    </div>
  );
}
